import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppealServiceResponse, IncorrectObjectForm, NewObjectForm, formDataRequest } from '@shared/components/appeals/models';
import { Store } from '@ngxs/store';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { ServerLinks } from '@shared/services/server-links';
import { ServerLinkName } from '@shared/models/server-links-model';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { MirSnackbarType } from '@shared/components/snackbar-message/model/snackbar';
import { SnackBarService } from '@shared/components/snackbar-message/services/snackbar.service';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { CatalogState } from '../../../../data/store/catalog/catalog.state';

@Injectable()
export class AppealsService {
  private _openDataLink: string;

  public resetMap = new Subject<boolean>();
  constructor(
    private http: HttpClient,
    private store: Store,
    private serverLinks: ServerLinks,
    private snackBar: SnackBarService,
    @Inject(DOCUMENT) private document: Document,
    private activeRoute: ActivatedRoute
  ) {
    this._openDataLink = serverLinks.getLink(ServerLinkName.OpenData);
  }

  private convertFormValueToServerRequestBodyNewForm(formValue: NewObjectForm): formDataRequest {
    const selectedCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
    const versions = this.store.selectSnapshot(CatalogState.currentCatalogVersionRelease);
    const datasetUrl = `${this.document.location.origin}/opendata/${selectedCatalog.datasetId}`;
    const version = versions?.versionReleaseInfo?.versionNum.toString() ?? '14';
    const release = versions?.versionReleaseInfo?.releaseNum.toString() ?? '14';
    const releaseDate = versions?.versionReleaseInfo?.releaseDate ?? '';
    const params = {
      user: {
        alias: `${formValue.name} ${formValue.surname}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        middle_name: formValue.secondName,
        email: formValue.email,
        phone: formValue.telephone ?? null
      },
      attrs: [
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          issue_attribute_code: 'opendata_dataset_id',
          value: selectedCatalog.datasetId
        },
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          issue_attribute_code: 'opendata_dataset',
          value: selectedCatalog.catalogName
        },
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          issue_attribute_code: 'opendata_object_name',
          value: formValue.nameItem
        },
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          issue_attribute_code: 'opendata_dataset_oiv',
          value: selectedCatalog.idOIV ?? ''
        },
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          issue_attribute_code: 'opendata_version_number',
          value: version
        },
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          issue_attribute_code: 'opendata_release_number',
          value: release
        }
      ],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      theme_id: formValue.themeId,
      text: `${
        formValue.comment
      }, Версия: ${version}, Релиз: ${release}, Дата релиза: ${releaseDate},  Ссылка на набор: ${datasetUrl}, Атрибуты объекта: ${formValue?.attributes
        ?.map(a => `'${a.attribute}':'${a.value}'`)
        .join(',')}`
    };

    if (formValue.address) {
      params.attrs.push({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        issue_attribute_code: 'opendata_address',
        value: formValue.address
      });
    }
    if (formValue.coordinateX) {
      params.attrs.push({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        issue_attribute_code: 'opendata_longitude',
        value: formValue.coordinateX
      });
    }
    if (formValue.coordinateY) {
      params.attrs.push({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        issue_attribute_code: 'opendata_latitude',
        value: formValue.coordinateY
      });
    }

    return params;
  }

  private convertFormValueToServerRequestBodyIncorrectForm(formValue: IncorrectObjectForm): formDataRequest {
    const params = this.activeRoute.snapshot.queryParams;
    const catalogItemGlobalId = params?.catalogItemGlobalId;
    const selectedCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
    const selectedCatalogItems = this.store.selectSnapshot(CatalogState.selectCatalogItem);
    const versions = this.store.selectSnapshot(CatalogState.currentCatalogVersionRelease);
    const datasetUrl = `${this.document.location.origin}/opendata/${selectedCatalog.datasetId}?objectId=${catalogItemGlobalId}&&filter=global_id%3D${catalogItemGlobalId}`;

    const version = versions?.versionReleaseInfo?.versionNum.toString() ?? '14';
    const release = versions?.versionReleaseInfo?.releaseNum.toString() ?? '14';
    const releaseDate = versions?.versionReleaseInfo?.releaseDate ?? '';
    return {
      user: {
        alias: `${formValue.name} ${formValue.surname}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        middle_name: formValue.secondName,
        email: formValue.email,
        phone: formValue.telephone ?? null
      },
      attrs: [
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          issue_attribute_code: 'opendata_dataset_id',
          value: selectedCatalog.datasetId
        },
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          issue_attribute_code: 'opendata_dataset',
          value: selectedCatalog.catalogName
        },
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          issue_attribute_code: 'opendata_row',
          value: selectedCatalogItems.number ?? 1
        },
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          issue_attribute_code: 'opendata_dataset_oiv',
          value: selectedCatalog.idOIV ?? ''
        },
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          issue_attribute_code: 'opendata_version_number',
          value: version
        },
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          issue_attribute_code: 'opendata_release_number',
          value: release
        }
      ],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      theme_id: formValue.themeId,
      text: `${
        formValue.comment
      }, Версия: ${version}, Релиз: ${release}, Дата релиза: ${releaseDate},  Ссылка на набор: ${datasetUrl}, Атрибуты объекта: ${formValue?.attributes
        ?.map(a => `'${a.attribute}':'${a.value}'`)
        .join(',')}`
    };
  }

  private snackBarError(error: string): void {
    this.snackBar.open(MirSnackbarType.Error, [
      {
        message: 'SNACK_BAR_MESSAGE.ERROR_SEND_APPEALS',
        translate: true
      },
      {
        message: `: ${error}`,
        translate: false
      }
    ]);
  }

  private sendForm(formValue: formDataRequest): Observable<AppealServiceResponse> {
    return this.http.post<AppealServiceResponse>(`${this._openDataLink}issues/issue/add`, formValue).pipe(
      tap(res => {
        if (res.code !== 200) {
          this.snackBarError(res.message);
        }
      }),
      catchError(err => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.snackBarError(err.error.message);
        return throwError(err);
      })
    );
  }

  public formSubmission(formType: 'new' | 'incorrect', formValue: NewObjectForm | IncorrectObjectForm): Observable<AppealServiceResponse> {
    const formData: formDataRequest =
      formType === 'new'
        ? this.convertFormValueToServerRequestBodyNewForm(formValue as NewObjectForm)
        : this.convertFormValueToServerRequestBodyIncorrectForm(formValue as IncorrectObjectForm);
    return this.sendForm(formData);
  }
}
